import { TriangleAlert } from "lucide-react";
import { VisuallyHiddenBankIdDialogHeaderElements } from "@/components/bank-id/components/dialog/bank-id-dialog-header-elements";
import { BankIdButton } from "@/components/bank-id/components/utilities/bank-id-button";
import { BankIdDialogContentHeader } from "@/components/bank-id/components/utilities/header";
import type { CloseModalProps } from "@/components/bank-id/types";
import { VisuallyHiddenDialogDescription } from "@/components/bank-id/utilities/visually-hidden-dialog-description";
import { DialogContent } from "@/components/ui/dialog";
import { useTranslations } from "@/hooks/use-translations";
import {
  useBankIdActions,
  useBankIdStore,
} from "@/stores/bank-id/bank-id-store";
import type { BankIdState } from "@/stores/bank-id/bank-id-store.types";
import type { AuthProcess } from "@/types";

type FailedAuthenticationProps = CloseModalProps & {
  authProcess: AuthProcess;
};

export function FailedAuthentication({
  authProcess,
  closeModal,
}: FailedAuthenticationProps) {
  const { __ } = useTranslations();
  const { handleCreateAuthenticationOrder, handleActivateAuthenticationOrder } =
    useBankIdActions();

  const { errorMessage, openOnThisDevice } = useBankIdStore(
    (state: BankIdState) => state
  );

  const retryAuthenticationOrder = async () => {
    await handleCreateAuthenticationOrder(authProcess, null, openOnThisDevice);
    await handleActivateAuthenticationOrder();
  };

  return (
    <DialogContent>
      <VisuallyHiddenBankIdDialogHeaderElements />
      <VisuallyHiddenDialogDescription />

      <div
        className={
          "mx-auto mb-8 flex w-max items-center justify-center rounded-lg bg-destructive/10"
        }
      >
        <div className={"p-6"}>
          <TriangleAlert className={"fill-destructive text-white"} />
        </div>
      </div>

      <div className="mb-8 text-center">
        <BankIdDialogContentHeader label={__("bank_id.error.title")} />
        <span>{errorMessage || __("bank_id.error.description")}</span>
      </div>

      <div className={"grid grid-cols-2 gap-x-2"}>
        <BankIdButton onClick={closeModal} variant={"ghost"}>
          {__("bank_id.cancel")}
        </BankIdButton>
        <BankIdButton onClick={retryAuthenticationOrder} variant={"secondary"}>
          {__("bank_id.try_again")}
        </BankIdButton>
      </div>
    </DialogContent>
  );
}
